import React from "react";
import { Form, Input, Select, Card, Button, Row, Col, Switch, Divider, Tag, Tooltip } from "antd";
import { PlusOutlined, MinusCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";

const contextOptions = [
  { label: "Branch", value: "branch" },
  { label: "Region", value: "region" },
  { label: "Global", value: "global" },
];

const approvalModeOptions = [
  { label: "First Response — any one approver advances step", value: "FIRST_RESPONSE" },
  { label: "Sequential — first actor in pool decides", value: "SEQUENTIAL" },
  { label: "All Required — every approver must approve", value: "ALL_REQUIRED" },
  { label: "Majority — 50%+ must approve", value: "MAJORITY" },
  { label: "Parallel (coming soon)", value: "PARALLEL", disabled: true },
];

const escalationStrategyOptions = [
  { label: "Reassign", value: "REASSIGN" },
  { label: "Notify Only", value: "NOTIFY_ONLY", disabled: true },
  { label: "Auto Approve", value: "AUTO_APPROVE", disabled: true },
  { label: "Auto Reject", value: "AUTO_REJECT", disabled: true },
  { label: "Skip", value: "SKIP", disabled: true },
];

// Resolver config fields — rendered per resolver entry inside a step
const ResolverConfigFields = ({ stepName, resolverIndex, roleOptions, userOptions, restField }) => (
  <Form.Item
    noStyle
    shouldUpdate={(prev, curr) =>
      prev.steps?.[stepName]?.resolvers?.[resolverIndex]?.assignmentStrategy !==
      curr.steps?.[stepName]?.resolvers?.[resolverIndex]?.assignmentStrategy
    }
  >
    {({ getFieldValue }) => {
      const strategy = getFieldValue(["steps", stepName, "resolvers", resolverIndex, "assignmentStrategy"]);

      if (strategy === "PERMISSION_ROLE") {
        return (
          <Row gutter={12} style={{ marginTop: 8 }}>
            <Col xs={24} md={10}>
              <Form.Item
                {...restField}
                label="Permission Role"
                name={[resolverIndex, "resolverConfig", "roleKey"]}
                rules={[{ required: true, message: "Role required" }]}
              >
                <Select options={roleOptions} placeholder="Select role" />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                {...restField}
                label="Context Level"
                name={[resolverIndex, "resolverConfig", "contextLevel"]}
                initialValue="branch"
                rules={[{ required: true, message: "Context required" }]}
              >
                <Select options={contextOptions} placeholder="Select context" />
              </Form.Item>
            </Col>
          </Row>
        );
      }

      if (strategy === "SPECIFIC_USER") {
        return (
          <Row gutter={12} style={{ marginTop: 8 }}>
            <Col xs={24} md={20}>
              <Form.Item
                {...restField}
                label="Specific User"
                name={[resolverIndex, "resolverConfig", "userId"]}
                rules={[{ required: true, message: "User required" }]}
              >
                <Select
                  showSearch
                  placeholder="Select user"
                  options={userOptions}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        );
      }

      if (strategy === "BRANCH_MANAGER") {
        return (
          <p style={{ color: "#888", marginTop: 8 }}>
            <em>Resolves to the manager of the context branch. No config needed.</em>
          </p>
        );
      }

      return null;
    }}
  </Form.Item>
);

// Lifecycle status map for INVENTORY_TRANSFER_APPROVAL steps
const STEP_LIFECYCLE_BADGE = {
  'Approval':     { color: 'blue',   label: 'CREATED → APPROVED' },
  'Packing':      { color: 'cyan',   label: 'APPROVED → PACKED' },
  'Dispatch':     { color: 'orange', label: 'PACKED → DISPATCHED' },
  'Receive':      { color: 'gold',   label: 'DISPATCHED → RECEIVED' },
  'Verification': { color: 'lime',   label: 'RECEIVED → VERIFIED' },
  'Closure':      { color: 'green',  label: 'VERIFIED → CLOSED' },
};


/**
 * "This step records the bank UTR" — payment workflows only.
 *
 * A bank payout's UTR only exists once someone has actually made the transfer, so
 * it is collected from an approver rather than from the maker. By default that is
 * the step that finishes the workflow, which is correct whenever the last approver
 * is also the one who pays. Tick this on an earlier step when the person who
 * releases the money is not the last to sign off — "Finance pays" ahead of a
 * "Director sign-off" step, say.
 *
 * Only one step can hold it, so ticking one unticks the rest.
 */
const PaymentReferenceToggle = ({ name, restField }) => {
  const form = Form.useFormInstance();

  const handleChange = (checked) => {
    if (!checked) return;
    const steps = form.getFieldValue("steps") || [];
    form.setFieldsValue({
      steps: steps.map((step, i) =>
        i === name ? step : { ...step, capturesPaymentReference: false }
      ),
    });
  };

  return (
    <Col xs={24} style={{ display: "flex", alignItems: "center", marginTop: 4 }}>
      <Form.Item
        {...restField}
        name={[name, "capturesPaymentReference"]}
        valuePropName="checked"
        initialValue={false}
        noStyle
      >
        <Switch onChange={handleChange} />
      </Form.Item>
      <span style={{ marginLeft: 8 }}>Records the bank UTR</span>
      <Tooltip
        title={
          "Tick this on the step whose approvers actually make the transfer. They "
          + "are asked for the UTR of each bank payment when they approve, and later "
          + "steps see it read-only. Leave it off and the last step is asked, which "
          + "is right when the final approver is also the one who pays. The money "
          + "still posts when the whole workflow completes, not at this step."
        }
      >
        <InfoCircleOutlined style={{ marginLeft: 6, color: "#8c8c8c" }} />
      </Tooltip>
    </Col>
  );
};

const ApproversSection = ({ roleOptions, strategyOptions, userOptions, fixedSteps = false, entityType }) => (
  <Form.List name="steps">
    {(fields, { add: addStep, remove: removeStep }) => (
      <>
        {fields.map(({ key, name, ...restField }, idx) => (
          <Card
            key={key}
            type="inner"
            title={
              <span>
                Step {idx + 1}
                <Form.Item noStyle shouldUpdate={(p, c) => {
                  if (fixedSteps) {
                    return p.steps?.[name]?.stepName !== c.steps?.[name]?.stepName;
                  }
                  return p.steps?.[name]?.approvalMode !== c.steps?.[name]?.approvalMode;
                }}>
                  {({ getFieldValue }) => {
                    if (fixedSteps) {
                      const stepName = getFieldValue(['steps', name, 'stepName']);
                      const badge = STEP_LIFECYCLE_BADGE[stepName];
                      return badge ? (
                        <Tag color={badge.color} style={{ marginLeft: 8, fontSize: 11 }}>{badge.label}</Tag>
                      ) : null;
                    }
                    const mode = getFieldValue(['steps', name, 'approvalMode']);
                    const modeTag = {
                      FIRST_RESPONSE: <Tag color="blue" style={{ marginLeft: 8 }}>First Response</Tag>,
                      ALL_REQUIRED: <Tag color="red" style={{ marginLeft: 8 }}>All Required</Tag>,
                      MAJORITY: <Tag color="orange" style={{ marginLeft: 8 }}>Majority</Tag>,
                    };
                    return modeTag[mode] || null;
                  }}
                </Form.Item>
              </span>
            }
            style={{ marginBottom: 16 }}
            extra={
              !fixedSteps && fields.length > 1 ? (
                <Button
                  danger
                  size="small"
                  icon={<MinusCircleOutlined />}
                  onClick={() => removeStep(name)}
                >
                  Remove
                </Button>
              ) : null
            }
          >
            {/* Step top row */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  {...restField}
                  label="Step Name"
                  name={[name, "stepName"]}
                  rules={[{ required: true, message: "Step name required" }]}
                >
                  <Input placeholder="e.g. Manager Approval" disabled={fixedSteps} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  {...restField}
                  label="Deadline (hours)"
                  name={[name, "deadlineHours"]}
                >
                  <Input type="number" min={1} placeholder="24" />
                </Form.Item>
              </Col>
            </Row>

            {/* Resolvers — nested list */}
            <Form.List name={[name, "resolvers"]}>
              {(resolverFields, { add: addResolver, remove: removeResolver }) => (
                <>
                  {resolverFields.map(({ key: rKey, name: rName, ...rRestField }, rIdx) => (
                    <Card
                      key={rKey}
                      size="small"
                      style={{ background: "#fafafa", marginBottom: 10 }}
                      title={
                        <span style={{ fontSize: 13, color: "#555" }}>
                          Approver Pool {rIdx + 1}
                          {resolverFields.length > 1 && (
                            <Tag color="cyan" style={{ marginLeft: 8, fontSize: 11 }}>
                              merged into step
                            </Tag>
                          )}
                        </span>
                      }
                      extra={
                        resolverFields.length > 1 ? (
                          <Button
                            size="small"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => removeResolver(rName)}
                          />
                        ) : null
                      }
                    >
                      <Row gutter={12}>
                        <Col xs={24} md={10}>
                          <Form.Item
                            {...rRestField}
                            label="Assignment Strategy"
                            name={[rName, "assignmentStrategy"]}
                            rules={[{ required: true, message: "Strategy required" }]}
                          >
                            <Select options={strategyOptions} placeholder="Select strategy" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <ResolverConfigFields
                        stepName={name}
                        resolverIndex={rName}
                        roleOptions={roleOptions}
                        userOptions={userOptions}
                        restField={rRestField}
                      />
                    </Card>
                  ))}

                  <Button
                    type="dashed"
                    size="small"
                    onClick={() => addResolver()}
                    icon={<PlusOutlined />}
                    style={{ marginBottom: 12 }}
                  >
                    Add Approver Pool
                  </Button>
                  {resolverFields.length > 1 && (
                    <p style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>
                      Multiple pools are merged — all users across pools can act on this step.
                      Set Approval Mode to <strong>First Response</strong> so any one approval advances the step.
                    </p>
                  )}
                </>
              )}
            </Form.List>

            <Divider dashed style={{ margin: "12px 0" }} />

            {/* Mode hint */}
            <Form.Item noStyle shouldUpdate={(p, c) => p.steps?.[name]?.approvalMode !== c.steps?.[name]?.approvalMode}>
              {({ getFieldValue }) => {
                const mode = getFieldValue(["steps", name, "approvalMode"]);
                const hints = {
                  ALL_REQUIRED: "Every approver in all pools must approve. Any single rejection ends the chain.",
                  MAJORITY: "More than 50% of approvers must approve. Outcome locks as soon as majority is impossible to flip.",
                  FIRST_RESPONSE: "First approver to act decides. Other approvers are superseded.",
                };
                return hints[mode] ? (
                  <p style={{ fontSize: 12, color: "#666", marginBottom: 12, background: "#f6f8fa", padding: "6px 10px", borderRadius: 4 }}>
                    {hints[mode]}
                  </p>
                ) : null;
              }}
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  {...restField}
                  label="Approval Mode"
                  name={[name, "approvalMode"]}
                  initialValue="SEQUENTIAL"
                  rules={[{ required: true, message: "Approval mode required" }]}
                >
                  <Select options={approvalModeOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  {...restField}
                  label="Escalation Strategy"
                  name={[name, "escalationStrategy"]}
                  initialValue="REASSIGN"
                  rules={[{ required: true, message: "Escalation strategy required" }]}
                >
                  <Select options={escalationStrategyOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} style={{ display: "flex", alignItems: "center", paddingTop: 30 }}>
                <Form.Item
                  {...restField}
                  name={[name, "commentsRequired"]}
                  valuePropName="checked"
                  initialValue={false}
                  noStyle
                >
                  <Switch />
                </Form.Item>
                <span style={{ marginLeft: 8 }}>Comments Required</span>
              </Col>
              {entityType === "PAYMENT_APPROVAL" && (
                <PaymentReferenceToggle name={name} restField={restField} />
              )}
            </Row>
          </Card>
        ))}

        {!fixedSteps && (
          <Form.Item>
            <Button
              type="dashed"
              onClick={() => addStep({ approvalMode: "SEQUENTIAL", escalationStrategy: "REASSIGN", commentsRequired: false, resolvers: [{}] })}
              block
              icon={<PlusOutlined />}
            >
              Add Step
            </Button>
          </Form.Item>
        )}
      </>
    )}
  </Form.List>
);

export default ApproversSection;
